import React, { Component } from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import { Auth } from 'aws-amplify';
import { Typography, withStyles } from '@material-ui/core';
import red from '@material-ui/core/colors/red';
import LoaderButton from './LoaderButton';
import ContentHeader from './ContentHeader';
import globalStyles from '../styles';

const styles = theme => ({
    root: {
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)',
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        position: 'relative',
        textAlign: 'center',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px 10px'
    },
    input: {
        margin: '20px 20px 10px'
    },
    button: {
        width: '200px',
        height: '56px',
        margin: '0 auto'
    },
    error: {
        margin: '0 20px 10px',
        color: red[500],
        minHeight: '20px'
    },
    link: {
        ...globalStyles.link,
        fontWeight: 'bold',
        textDecoration: 'underline',
        cursor: 'pointer'
    },
    disabledLink: {
        color: '#666',
        fontWeight: 'bold'
    },
    paragraph: {
        fontSize: '16px',
        marginBottom: '10px'
    }
});

class ConfirmationForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            error: null,
            loading: false,
            confirmationCode: '',
            hasResentCode: false
        };
    }

    handleConfirmationSubmit = async (event, retryIfQuotaExceeded = true) => {
        event.preventDefault();
        const { email, password, handleAuth } = this.props;
        const { confirmationCode } = this.state;

        this.setState({ loading: true });

        try {
            await Auth.confirmSignUp(
                email.toLowerCase(),
                confirmationCode.trim()
            );
            await Auth.signIn(email.toLowerCase(), password);

            handleAuth(true);
        } catch (e) {
            if (e.name === 'QuotaExceededError' && retryIfQuotaExceeded) {
                window.localStorage.clear();

                await this.handleConfirmationSubmit(event, false);
                return;
            }

            let err = e.message;

            if (
                err === 'Invalid verification code provided, please try again.'
            ) {
                err =
                    "Invalid verification code. Please enter the very latest code we sent you, older codes won't work.";
            }

            this.setState({
                error: e.message
            });
            this.setState({ loading: false });
        }
    };

    handleChange = event => {
        this.setState({
            confirmationCode: event.target.value
        });
    };

    displayError = message => {
        if (!message) {
            return <></>;
        }

        if (
            message === 'Invalid verification code provided, please try again.'
        ) {
            return (
                <>
                    Invalid verification code. Please enter only the very latest
                    code we sent you; older codes won{"'"}t work.{' '}
                    <a
                        style={{ fontWeight: 'bold', color: 'red' }}
                        href="https://help.shuttleid.uk/en/articles/7851782-confirm-your-account#h_d45d061e7e"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        More info
                    </a>
                    .
                </>
            );
        }

        return <>{message}</>;
    };

    resendCode = async () => {
        const { email } = this.props;
        this.setState({
            hasResentCode: true
        });

        await Auth.resendSignUp(email.toLowerCase());

        try {
            window.localStorage.setItem(
                'confirmation_code_sent',
                new Date().toISOString()
            );
        } catch (localStorageErr) {
            console.error(localStorageErr);
        }
    };

    validateConfirmationForm() {
        const { confirmationCode } = this.state;

        return confirmationCode.length > 0;
    }

    render() {
        const { confirmationCode, error, loading, hasResentCode } = this.state;
        const { classes, email } = this.props;

        return (
            <div className={classes.root}>
                <ContentHeader text="Confirm email" id="confirm-email-header" />
                <form
                    className={classes.form}
                    onSubmit={this.handleConfirmationSubmit}
                >
                    <Typography variant="body1" className={classes.paragraph}>
                        Please enter the code sent to <strong>{email}</strong>.
                    </Typography>
                    <Typography variant="body1" className={classes.paragraph}>
                        <strong>NOTE:</strong> This email could arrive in your
                        junk folder. Please double check this first.
                    </Typography>
                    <Typography variant="body1" className={classes.paragraph}>
                        <a
                            className={classes.link}
                            href="https://help.shuttleid.uk/en/articles/7851782-confirm-your-account"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Help confirming your account
                        </a>
                    </Typography>
                    <Typography variant="body1" className={classes.paragraph}>
                        {!hasResentCode && (
                            <span
                                onClick={this.resendCode}
                                className={classes.link}
                                aria-hidden="true"
                            >
                                Send another code
                            </span>
                        )}
                        {hasResentCode && (
                            <span className={classes.disabledLink}>
                                New code sent. Please check your inbox.
                            </span>
                        )}
                    </Typography>
                    <TextField
                        autoFocus
                        autoComplete="off"
                        className={classes.input}
                        id="confirmationCode"
                        label="Confirmation Code"
                        value={confirmationCode}
                        onChange={this.handleChange}
                        variant="outlined"
                    />
                    <Typography className={classes.error}>
                        {this.displayError(error)}
                    </Typography>
                    <LoaderButton
                        variant="outlined"
                        color="primary"
                        disabled={!this.validateConfirmationForm()}
                        className={classes.button}
                        type="submit"
                        isLoading={loading}
                        text="Verify"
                        loadingText="Verifying.."
                    />
                </form>
            </div>
        );
    }
}

ConfirmationForm.propTypes = {
    classes: PropTypes.shape().isRequired
};

export default withStyles(styles)(ConfirmationForm);
